<template>
  <basic-page title="Tarveilmoitukset">
    <v-row class="fill-height">
      <v-col cols="12" lg="8" offset-lg="2" xl="8" offset-xl="2">
        <v-card>
          <v-card-title>
            Tarveilmoitukset
            <v-spacer />
            <v-btn
              v-if="$isKalustoTarveilmoittaja"
              color="primary"
              @click="openAddDialog"
            >
              <v-icon left>mdi-plus</v-icon>
              Lisää ilmoitus
            </v-btn>
          </v-card-title>
          <v-card-subtitle>
            Tällä lomakkeella ilmoitetaan pelastustoiminnan ja VPK:n
            tarvitsemista tarvikkeista ja varusteista. Kirjauksen tekee
            huoltoryhmän ryhmänjohtaja. Vastuuhenkilö saa ilmoituksen
            automaattisesti ja päivittää hankintastatuksen sitä mukaa kun tilaus
            etenee.
          </v-card-subtitle>
          <v-divider />
          <v-card-text>
            <!-- Filter chips -->
            <v-chip-group
              v-model="statusFilter"
              class="mb-3"
              active-class="primary--text"
            >
              <v-chip filter outlined value="avoimet">Avoimet</v-chip>
              <v-chip filter outlined value="kaikki">Kaikki</v-chip>
              <v-chip filter outlined value="valmiit"
                >Valmiit (Asemalla)</v-chip
              >
            </v-chip-group>

            <!-- Desktop table -->
            <v-data-table
              v-if="$vuetify.breakpoint.mdAndUp"
              :items="filteredItems"
              :headers="headers"
              :loading="loading"
              sort-by="kirjauspaiva"
              sort-desc
              no-data-text="Ei tarveilmoituksia"
              :items-per-page="999"
              hide-default-footer
            >
              <template v-slot:item.kirjauspaiva="{ item }">
                {{ $formatDate(item.kirjauspaiva) }}
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip :color="statusColor(item.status)" dark small>
                  {{ item.status }}
                </v-chip>
                <div v-if="item.updatedBy" class="caption grey--text mt-1">
                  {{ item.updatedBy.user }}
                  <span v-if="item.updatedAt"
                    >&nbsp;&bull;&nbsp;{{ $formatDate(item.updatedAt) }}</span
                  >
                </div>
              </template>
              <template v-slot:item.hankintapaiva="{ item }">
                {{ item.hankintapaiva ? $formatDate(item.hankintapaiva) : "-" }}
              </template>
              <template v-slot:item.createdBy="{ item }">
                {{ item.createdBy ? item.createdBy.user : "" }}
              </template>
              <template v-slot:item.vastuuhenkilot="{ item }">
                <template
                  v-if="item.vastuuhenkilot && item.vastuuhenkilot.length"
                >
                  <v-chip
                    v-for="email in item.vastuuhenkilot"
                    :key="email"
                    x-small
                    class="mr-1 mb-1"
                  >
                    {{ vastuuhenkiloName(email) }}
                  </v-chip>
                </template>
                <span v-else class="grey--text">Kaikki</span>
              </template>
              <template v-slot:item.statusKuvaus="{ item }">
                <span class="text-pre-wrap">{{
                  item.statusKuvaus || "-"
                }}</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn
                  v-if="
                    $isTarveilmoitusVastuuhenkilo && !itemHasVastuuhenkilo(item)
                  "
                  x-small
                  color="info"
                  class="mr-1"
                  @click.stop="confirmOtaVastuu(item)"
                  title="Ota vastuu"
                >
                  <v-icon x-small left>mdi-hand-back-left</v-icon>
                  Ota vastuu
                </v-btn>
                <v-btn
                  icon
                  small
                  @click.stop="openEditDialog(item)"
                  v-if="
                    $isKalustoTarveilmoittaja || $isTarveilmoitusVastuuhenkilo
                  "
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  @click.stop="confirmDelete(item)"
                  v-if="$isKalustoTarveilmoittaja"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <!-- Mobile list -->
            <template v-else>
              <v-progress-linear v-if="loading" indeterminate class="mb-2" />
              <div
                v-if="!loading && filteredItems.length === 0"
                class="text-center pa-4 grey--text"
              >
                Ei tarveilmoituksia
              </div>
              <v-list v-else class="pa-0">
                <v-list-item
                  v-for="(item, index) in filteredItems"
                  :key="item.id"
                  @click="openEditDialog(item)"
                  class="align-start py-3"
                  :class="{ 'border-bottom': index < filteredItems.length - 1 }"
                >
                  <v-list-item-content>
                    <div class="d-flex align-center mb-1">
                      <span class="font-weight-medium body-1">{{
                        item.tarvike
                      }}</span>
                      <v-spacer />
                      <v-chip
                        :color="statusColor(item.status)"
                        dark
                        x-small
                        class="ml-2 flex-shrink-0"
                      >
                        {{ item.status }}
                      </v-chip>
                    </div>
                    <div class="caption grey--text mb-1">
                      {{ $formatDate(item.kirjauspaiva) }}
                      <span v-if="item.createdBy">
                        &bull; {{ item.createdBy.user }}</span
                      >
                    </div>
                    <div
                      v-if="item.kuvaus"
                      class="body-2 mt-1"
                      style="white-space: normal"
                    >
                      {{ item.kuvaus }}
                    </div>
                    <div
                      v-if="item.vastuuhenkilot && item.vastuuhenkilot.length"
                      class="caption mt-1"
                    >
                      <v-icon x-small class="mr-1">mdi-account-multiple</v-icon>
                      <v-chip
                        v-for="email in item.vastuuhenkilot"
                        :key="email"
                        x-small
                        class="mr-1"
                      >
                        {{ vastuuhenkiloName(email) }}
                      </v-chip>
                    </div>
                    <div
                      v-if="
                        item.hankintapaiva ||
                        item.statusKuvaus ||
                        item.updatedBy
                      "
                      class="mt-2"
                    >
                      <v-divider class="mb-2" />
                      <div v-if="item.hankintapaiva" class="caption">
                        <v-icon x-small class="mr-1">mdi-calendar-check</v-icon>
                        Hankintapäivä: {{ $formatDate(item.hankintapaiva) }}
                      </div>
                      <div
                        v-if="item.statusKuvaus"
                        class="caption mt-1"
                        style="white-space: normal"
                      >
                        <v-icon x-small class="mr-1"
                          >mdi-comment-text-outline</v-icon
                        >
                        {{ item.statusKuvaus }}
                      </div>
                      <div
                        v-if="item.updatedBy"
                        class="caption grey--text mt-1"
                      >
                        <v-icon x-small class="mr-1"
                          >mdi-account-edit-outline</v-icon
                        >
                        {{ item.updatedBy.user }}
                        <span v-if="item.updatedAt"
                          >&nbsp;&bull;&nbsp;{{
                            $formatDate(item.updatedAt)
                          }}</span
                        >
                      </div>
                    </div>
                    <div
                      v-if="
                        $isTarveilmoitusVastuuhenkilo &&
                        !itemHasVastuuhenkilo(item)
                      "
                      class="mt-2"
                    >
                      <v-btn
                        x-small
                        color="info"
                        @click.stop="confirmOtaVastuu(item)"
                      >
                        <v-icon x-small left>mdi-hand-back-left</v-icon>
                        Ota vastuu
                      </v-btn>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </v-card-text>

          <!-- Add/Edit Dialog -->
          <v-dialog
            v-model="dialog"
            max-width="600"
            persistent
            :fullscreen="$vuetify.breakpoint.xsOnly"
          >
            <v-card>
              <v-card-title>
                {{ editMode ? "Muokkaa ilmoitusta" : "Uusi tarveilmoitus" }}
                <v-spacer />
                <v-btn
                  icon
                  @click="closeDialog"
                  v-if="$vuetify.breakpoint.xsOnly"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-card-text class="pt-4">
                <v-form ref="form">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="
                          formItem.kirjauspaiva
                            ? $formatDate(formItem.kirjauspaiva)
                            : ''
                        "
                        label="Kirjauspäivämäärä"
                        prepend-icon="mdi-calendar"
                        readonly
                        outlined
                        v-bind="attrs"
                        v-on="on"
                        :rules="[rules.required]"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formItem.kirjauspaiva"
                      @input="dateMenu = false"
                      locale="fi"
                      first-day-of-week="1"
                    ></v-date-picker>
                  </v-menu>

                  <v-text-field
                    v-model="formItem.tarvike"
                    label="Tarvittava tarvike"
                    outlined
                    :rules="[rules.required]"
                  ></v-text-field>

                  <v-textarea
                    v-model="formItem.kuvaus"
                    label="Kuvaus (vapaaehtoinen)"
                    auto-grow
                    rows="2"
                    outlined
                  ></v-textarea>

                  <v-select
                    v-model="formItem.vastuuhenkilot"
                    :items="vastuuhenkiloOptions"
                    item-text="name"
                    item-value="email"
                    label="Vastuuhenkilö(t) (vapaaehtoinen)"
                    outlined
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    hint="Jos ei valittu, ilmoitus lähetetään kaikille vastuuhenkilöille"
                    persistent-hint
                  ></v-select>

                  <!-- Vastuuhenkilön osio -->
                  <template v-if="$isTarveilmoitusVastuuhenkilo && editMode">
                    <v-divider class="my-4" />
                    <div class="subtitle-1 font-weight-medium mb-2">
                      Vastuuhenkilön tiedot
                    </div>

                    <v-menu
                      v-model="hankintaDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="
                            formItem.hankintapaiva
                              ? $formatDate(formItem.hankintapaiva)
                              : ''
                          "
                          label="Hankintapäivämäärä"
                          prepend-icon="mdi-calendar-check"
                          readonly
                          clearable
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="formItem.hankintapaiva = null"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="formItem.hankintapaiva"
                        @input="hankintaDateMenu = false"
                        locale="fi"
                        first-day-of-week="1"
                      ></v-date-picker>
                    </v-menu>

                    <v-select
                      v-model="formItem.status"
                      :items="statuses"
                      label="Status"
                      outlined
                    ></v-select>

                    <v-textarea
                      v-model="formItem.statusKuvaus"
                      label="Vastuuhenkilön kuvaus (vapaaehtoinen)"
                      auto-grow
                      rows="2"
                      outlined
                    ></v-textarea>
                  </template>
                </v-form>
              </v-card-text>
              <v-divider />
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="closeDialog">Peruuta</v-btn>
                <v-btn color="primary" @click="save" :loading="saving">
                  Tallenna
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Ota vastuu Confirmation -->
          <v-dialog v-model="vastuuDialog" max-width="400">
            <v-card>
              <v-card-title>Ota vastuu</v-card-title>
              <v-card-text>
                Haluatko ottaa vastuun ilmoituksesta "{{
                  vastuuTarget?.tarvike
                }}"?
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="vastuuDialog = false">Peruuta</v-btn>
                <v-btn color="info" @click="otaVastuu" :loading="savingVastuu">
                  Ota vastuu
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Delete Confirmation -->
          <v-dialog v-model="deleteDialog" max-width="400">
            <v-card>
              <v-card-title>Poista ilmoitus</v-card-title>
              <v-card-text>
                Haluatko varmasti poistaa ilmoituksen "{{
                  deleteTarget?.tarvike
                }}"?
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="deleteDialog = false">Peruuta</v-btn>
                <v-btn color="error" @click="deleteItem" :loading="deleting">
                  Poista
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card>
      </v-col>
    </v-row>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import dayjs from "dayjs";
import {
  addDoc,
  collection,
  getDocs,
  query,
  updateDoc,
  where,
  doc,
} from "firebase/firestore";
import { firestore as db } from "@/plugins/firebase.app";
import { orderBy } from "lodash";

const COLLECTION = "kalusto_tarveilmoitukset";

export default {
  name: "KalustoTarveilmoituksetView",
  components: { BasicPage },
  data: () => ({
    items: [],
    loading: false,
    dialog: false,
    editMode: false,
    saving: false,
    deleteDialog: false,
    deleteTarget: null,
    deleting: false,
    vastuuDialog: false,
    vastuuTarget: null,
    savingVastuu: false,
    dateMenu: false,
    hankintaDateMenu: false,
    statusFilter: "avoimet",
    formItem: {
      kirjauspaiva: dayjs().format("YYYY-MM-DD"),
      tarvike: "",
      kuvaus: "",
      hankintapaiva: null,
      status: "Ilmoitettu",
      statusKuvaus: "",
      vastuuhenkilot: [],
    },
    vastuuhenkiloOptions: [
      { name: "Jarno Joensuu", email: "jarnojjoensuu@gmail.com" },
      { name: "Jarkko Ylinen", email: "jla.ylinen@gmail.com" },
      { name: "Kai Heikkilä", email: "heikkila.kai65@gmail.com" },
      { name: "Joni Leino", email: "joni.leino88@gmail.com" },
      { name: "Piia Kanersalo (Evy)", email: "pkanersalo@gmail.com" },
    ],
    statuses: [
      "Ilmoitettu",
      "Tilattu",
      "Odottaa toimitusta asemalle",
      "Asemalla",
    ],
    rules: {
      required: (v) => !!v || "Tämä kenttä on pakollinen",
    },
  }),
  computed: {
    headers() {
      const h = [
        { text: "Päivämäärä", value: "kirjauspaiva", width: 130 },
        { text: "Tarvike", value: "tarvike" },
        { text: "Kuvaus", value: "kuvaus" },
        { text: "Vastuuhenkilö", value: "vastuuhenkilot", width: 160 },
        { text: "Status", value: "status", width: 200 },
        { text: "Vastuuhenkilön kuvaus", value: "statusKuvaus" },
        { text: "Hankintapäivä", value: "hankintapaiva", width: 140 },
        { text: "Ilmoittaja", value: "createdBy", width: 150 },
      ];
      if (
        this.$isKalustoTarveilmoittaja ||
        this.$isTarveilmoitusVastuuhenkilo
      ) {
        h.push({ text: "", value: "actions", sortable: false, width: 180 });
      }
      return h;
    },
    filteredItems() {
      let items = this.items;
      if (this.statusFilter === "avoimet") {
        items = items.filter((i) => i.status !== "Asemalla");
      } else if (this.statusFilter === "valmiit") {
        items = items.filter((i) => i.status === "Asemalla");
      }
      return orderBy(items, ["kirjauspaiva"], ["desc"]);
    },
  },
  mounted() {
    this.fetchItems();
  },
  methods: {
    statusColor(status) {
      switch (status) {
        case "Ilmoitettu":
          return "orange";
        case "Tilattu":
          return "blue";
        case "Odottaa toimitusta asemalle":
          return "purple";
        case "Asemalla":
          return "green";
        default:
          return "grey";
      }
    },
    async fetchItems() {
      this.loading = true;
      try {
        const q = query(
          collection(db, COLLECTION),
          where("deleted", "==", false),
        );
        const snapshot = await getDocs(q);
        this.items = snapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
      } catch (e) {
        console.error(e);
        this.$toast("Tarveilmoitusten haku epäonnistui", { type: "error" });
      }
      this.loading = false;
    },
    openAddDialog() {
      this.editMode = false;
      this.formItem = {
        kirjauspaiva: dayjs().format("YYYY-MM-DD"),
        tarvike: "",
        kuvaus: "",
        hankintapaiva: null,
        status: "Ilmoitettu",
        statusKuvaus: "",
        vastuuhenkilot: [],
      };
      this.dialog = true;
    },
    openEditDialog(item) {
      if (
        !this.$isKalustoTarveilmoittaja &&
        !this.$isTarveilmoitusVastuuhenkilo
      )
        return;
      this.editMode = true;
      this.formItem = { ...item, vastuuhenkilot: item.vastuuhenkilot || [] };
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.$refs.form?.resetValidation();
    },
    async save() {
      if (!this.$refs.form?.validate()) return;

      this.saving = true;
      try {
        if (this.editMode && this.formItem.id) {
          const docRef = doc(db, COLLECTION, this.formItem.id);
          await updateDoc(docRef, {
            tarvike: this.formItem.tarvike,
            kuvaus: this.formItem.kuvaus || "",
            vastuuhenkilot: this.formItem.vastuuhenkilot || [],
            hankintapaiva: this.formItem.hankintapaiva || null,
            status: this.formItem.status,
            statusKuvaus: this.formItem.statusKuvaus || "",
            updatedBy: this.$userDBContext,
            updatedAt: dayjs().toISOString(),
          });
        } else {
          await addDoc(collection(db, COLLECTION), {
            kirjauspaiva: this.formItem.kirjauspaiva,
            tarvike: this.formItem.tarvike,
            kuvaus: this.formItem.kuvaus || "",
            vastuuhenkilot: this.formItem.vastuuhenkilot || [],
            hankintapaiva: null,
            status: "Ilmoitettu",
            statusKuvaus: "",
            createdBy: this.$userDBContext,
            createdAt: dayjs().toISOString(),
            updatedBy: null,
            updatedAt: null,
            deleted: false,
          });
        }
        this.$toast("Tallennettu", { type: "success" });
        this.closeDialog();
        await this.fetchItems();
      } catch (e) {
        console.error(e);
        this.$toast("Tallennus epäonnistui", { type: "error" });
      }
      this.saving = false;
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async deleteItem() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        const docRef = doc(db, COLLECTION, this.deleteTarget.id);
        await updateDoc(docRef, {
          deleted: true,
          deletedAt: dayjs().toISOString(),
          deletedBy: this.$userDBContext,
        });
        this.$toast("Ilmoitus poistettu", { type: "success" });
        this.deleteDialog = false;
        this.deleteTarget = null;
        await this.fetchItems();
      } catch (e) {
        console.error(e);
        this.$toast("Poisto epäonnistui", { type: "error" });
      }
      this.deleting = false;
    },
    vastuuhenkiloName(email) {
      const found = this.vastuuhenkiloOptions.find((v) => v.email === email);
      return found ? found.name : email;
    },
    itemHasVastuuhenkilo(item) {
      return (item.vastuuhenkilot || []).length > 0;
    },
    confirmOtaVastuu(item) {
      this.vastuuTarget = item;
      this.vastuuDialog = true;
    },
    async otaVastuu() {
      if (!this.vastuuTarget) return;
      this.savingVastuu = true;
      try {
        const currentEmail = this.$userInfo?.email;
        if (!currentEmail) return;
        const vastuuhenkilot = [...(this.vastuuTarget.vastuuhenkilot || [])];
        if (!vastuuhenkilot.includes(currentEmail)) {
          vastuuhenkilot.push(currentEmail);
        }
        const docRef = doc(db, COLLECTION, this.vastuuTarget.id);
        await updateDoc(docRef, {
          vastuuhenkilot,
          updatedBy: this.$userDBContext,
          updatedAt: dayjs().toISOString(),
        });
        this.$toast("Vastuu otettu", { type: "success" });
        this.vastuuDialog = false;
        this.vastuuTarget = null;
        await this.fetchItems();
      } catch (e) {
        console.error(e);
        this.$toast("Vastuun ottaminen epäonnistui", { type: "error" });
      }
      this.savingVastuu = false;
    },
  },
};
</script>
